/**
 * Deciding which CIS draft estimate an open Promo Office quote answers.
 *
 * The merch team types `MCH-…` onto the quote and the refresh links it. A
 * quote saved without the reference lands here (brief §8 J3) so no quote is
 * lost because someone forgot. Same shape as the customer-linking tab beside
 * it: a table of rows, one open at a time, the decision made in place, and
 * "Not a merchandise request" remembered so a quote leaves the lane for good.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  OpenDraftEstimateOption,
  UnlinkedPromoOfficeQuote,
} from "@shared-core/data/merchandise-types";
import type { PromoOfficeTender } from "@shared-core/data/adapter";
import {
  formatDateTimeOrDash,
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { paths } from "@shared-core/routing/routes";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Card } from "../../primitives/card";
import { ListPagination } from "../../patterns/ListPagination";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Link2,
  Loader2,
  X,
} from "lucide-react";

const PAGE_SIZE = 25;

/**
 * The "Linked to MCH-K01234" marker on the Quotes tab, linking to the draft it
 * names. The destination comes from the link's own `_self`
 * (`/draft-estimates/{id}`) — the id is read out of it rather than a URL being
 * built here, which is what `_self` exists to prevent.
 */
export function LinkedDraftEstimateMarker({
  link,
}: {
  link: PromoOfficeTender["linkedDraftEstimate"];
}) {
  if (!link) return null;
  return (
    <Link
      to={paths.estimateDetail({ id: link.id })}
      className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Badge variant="success" className="font-normal">
        <Link2 className="size-3" aria-hidden />
        Linked to <span className="font-mono">{link.reference}</span>
      </Badge>
    </Link>
  );
}

export function UnlinkedQuotesTab({
  onResolved,
}: {
  /** Lets the page refresh its tab counts once a quote leaves the list. */
  onResolved?: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [quotes, setQuotes] = useState<UnlinkedPromoOfficeQuote[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setQuotes(null);
    setLoadFailed(false);
    dataAdapter
      .listPromoOfficeUnlinkedQuotes({ page, pageSize: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setQuotes(response.items);
        setTotalPages(response.totalPages);
        setTotal(response.total);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, reloadTick]);

  const reload = useCallback(() => {
    setExpandedId(null);
    setReloadTick((n) => n + 1);
    onResolved?.();
  }, [onResolved]);

  if (loadFailed) {
    return (
      <Card className="p-12 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 mx-auto text-amber-600" />
        <p className="text-sm text-muted-foreground">
          The unlinked quotes couldn&apos;t be loaded.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setReloadTick((n) => n + 1)}
        >
          Try again
        </Button>
      </Card>
    );
  }

  if (quotes === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Check className="w-10 h-10 mx-auto mb-3 text-green-600 dark:text-green-400" />
        <h3 className="mb-1">Nothing to decide</h3>
        <p className="text-muted-foreground text-sm">
          Every open Promo Office quote either carries a merchandise request
          number or has been marked as not a merchandise request.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {total} open Promo Office {total === 1 ? "quote" : "quotes"} with no
        recognised merchandise request number. Newest first.
      </p>
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-6" />
              <TableHead>Quote</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Customer in One</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Lines</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotes.map((quote) => (
              <UnlinkedQuoteRow
                key={quote.promoOfficeQuoteId}
                quote={quote}
                expanded={expandedId === quote.promoOfficeQuoteId}
                onToggle={() =>
                  setExpandedId((current) =>
                    current === quote.promoOfficeQuoteId
                      ? null
                      : quote.promoOfficeQuoteId,
                  )
                }
                onResolved={reload}
              />
            ))}
          </TableBody>
        </Table>
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={(next) => {
            setExpandedId(null);
            setPage(next);
          }}
        />
      </div>
    </div>
  );
}

function UnlinkedQuoteRow({
  quote,
  expanded,
  onToggle,
  onResolved,
}: {
  quote: UnlinkedPromoOfficeQuote;
  expanded: boolean;
  onToggle: () => void;
  onResolved: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="w-6">
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-mono text-sm">
          {quote.promoOfficeQuoteNumber}
        </TableCell>
        <TableCell className="font-medium">
          {quote.customerName ?? "—"}
        </TableCell>
        <TableCell>
          {quote.cisCustomerCode ? (
            <span className="flex flex-col gap-0.5">
              <span>{quote.cisCustomerName ?? "Not found in One"}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {quote.cisCustomerCode}
              </span>
            </span>
          ) : (
            <Badge variant="warning" className="font-normal">
              No customer in One
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatQuotedPrice(quote.totalValuePence)}
          <span className="block text-[11px] text-muted-foreground">
            Promo Office sell
          </span>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {quote.linesCount} {quote.linesCount === 1 ? "line" : "lines"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatShortDateOrDash(quote.createdAt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/30 p-0">
            <div
              className="p-4 whitespace-normal"
              onClick={(e) => e.stopPropagation()}
            >
              <UnlinkedQuotePanel quote={quote} onResolved={onResolved} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function UnlinkedQuotePanel({
  quote,
  onResolved,
}: {
  quote: UnlinkedPromoOfficeQuote;
  onResolved: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [picking, setPicking] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await dataAdapter.dismissPromoOfficeUnlinkedQuote(
        quote.promoOfficeQuoteId,
      );
      toast.success(
        `${quote.promoOfficeQuoteNumber} marked as not a merchandise request`,
      );
      onResolved();
    } catch (err) {
      toast.error(mutationErrorMessage(err, "Could not mark this quote"));
      setDismissing(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <section className="space-y-1">
        <h4 className="text-sm font-medium">In Promo Office</h4>
        <p className="text-sm text-muted-foreground">
          Quote {quote.promoOfficeQuoteNumber} for{" "}
          {quote.customerName ?? "an unnamed customer"}
          {" · "}
          {formatQuotedPrice(quote.totalValuePence)} Promo Office sell
          {" · "}
          {quote.linesCount} {quote.linesCount === 1 ? "line" : "lines"}
          {" · "}
          created {formatDateTimeOrDash(quote.createdAt)}. No merchandise
          request number was typed onto it.
        </p>
      </section>

      <section className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={picking ? "default" : "outline"}
          onClick={() => setPicking((open) => !open)}
          disabled={dismissing}
        >
          <Link2 className="h-3.5 w-3.5" />
          Link to merchandise request…
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={dismiss}
          disabled={dismissing}
        >
          {dismissing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          Not a merchandise request
        </Button>
        <span className="text-xs text-muted-foreground self-center">
          Remembered — the quote leaves this lane and does not come back.
        </span>
      </section>

      {picking && (
        <DraftEstimatePicker
          quote={quote}
          onLinked={(reference) => {
            toast.success(
              `${quote.promoOfficeQuoteNumber} linked to ${reference}`,
            );
            onResolved();
          }}
        />
      )}
    </div>
  );
}

function DraftEstimatePicker({
  quote,
  onLinked,
}: {
  quote: UnlinkedPromoOfficeQuote;
  onLinked: (reference: string) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [options, setOptions] = useState<OpenDraftEstimateOption[] | null>(
    null,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  // Draft estimates still key on the customer's Tharstern code (v1 completion C5, ruling 5).
  const customerCode = quote.tharsternCustomerCode ?? null;

  useEffect(() => {
    if (!customerCode) return;
    let cancelled = false;
    setOptions(null);
    setLoadFailed(false);
    dataAdapter
      .listOpenMerchandiseDraftEstimates({ customerCode })
      .then((found) => {
        if (!cancelled) setOptions(found);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, customerCode]);

  if (!customerCode && quote.customerId) {
    return (
      <section className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
        Created in One: merchandise requests need a customer linked to Tharstern
        for now, so this quote has none to link.
      </section>
    );
  }

  if (!customerCode) {
    return (
      <section className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
        This quote&apos;s customer has no customer in One yet, so there is
        nothing to match its merchandise requests against. Decide who the
        customer is on the{" "}
        <strong className="font-medium text-foreground">
          No customer in One
        </strong>{" "}
        tab first, then come back to link the quote.
      </section>
    );
  }

  const link = async (option: OpenDraftEstimateOption) => {
    setLinkingId(option.id);
    try {
      await dataAdapter.linkPromoOfficeQuoteToDraftEstimate({
        promoOfficeQuoteId: quote.promoOfficeQuoteId,
        draftEstimateId: option.id,
      });
      onLinked(option.reference);
    } catch (err) {
      toast.error(mutationErrorMessage(err, "Could not link this quote"));
      setLinkingId(null);
    }
  };

  return (
    <section className="rounded-md border bg-background p-3 space-y-3">
      <div className="space-y-1">
        <h4 className="text-sm font-medium">
          Merchandise requests awaiting a quote for{" "}
          {quote.cisCustomerName ?? customerCode}
        </h4>
        <p className="text-xs text-muted-foreground">
          Only this customer&apos;s open requests are offered. Linking prices
          the request from this quote&apos;s buy price and resolves the
          merchandise team&apos;s ticket.
        </p>
      </div>

      {loadFailed && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          The merchandise requests could not be loaded just now. Try again in a
          moment.
        </p>
      )}

      {options === null && !loadFailed && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading merchandise
          requests…
        </div>
      )}

      {options !== null && options.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No merchandise request is awaiting a quote for this customer. If this
          is a merchandise request, raise it first with New merchandise request;
          otherwise mark the quote as not a merchandise request.
        </p>
      )}

      {options !== null && options.length > 0 && (
        <ul className="divide-y rounded-md border">
          {options.map((option) => (
            <li
              key={option.id}
              className="flex flex-wrap items-center justify-between gap-2 p-2"
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-sm">{option.reference}</span>
                  <span className="text-sm font-medium">
                    {option.projectName}
                  </span>
                </span>
                <span className="text-sm">
                  {option.requestLineCount === 1
                    ? "1 line"
                    : `${option.requestLineCount} lines`}
                </span>
                <span className="text-xs text-muted-foreground">
                  Raised {formatDateTimeOrDash(option.createdAt)}
                </span>
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={linkingId !== null}
                onClick={() => link(option)}
              >
                {linkingId === option.id && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Link
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
